import { useAppActions } from "../../../stores/application/useAppActions"
import { ModalActionType } from "../../../components/modal/AppModal"
import { ReactNode, useEffect, useRef } from "react"
import { useExtensionRequest } from "../../apis/extension/useExtensionRequest"
import { useAppStore } from "../../../stores/application/useAppStore"
import { ISidebar } from "../../../interfaces/store/appStore"
import { IUser } from "../../../interfaces/models/user"
import { IConnection } from "../../../interfaces/models/connection"
import { IEnrichedTransaction } from "../../../components/modal/modals/extension/CalculateEnrichmentModal"
import posthog from "posthog-js"
import { useAuthStore } from "../../../stores/auth/useAuthStore"

import { IKanban } from "../../../interfaces/models/kanban"
import { ICampaign, ISuppressionList, SuppressionType } from "../../../interfaces/models/campaign"

export interface IBulkEditTagsConnectionSnapshot {
    connectionId: string
    tags?: any[]
}

export const useAppHandlers = () => {
    const { setToast, setModal, setSpinner, setExtensionInstalled, setExtensionId, setExtensionVersion, setSidebar, setLeadDeltaTabIsActive, setOpenChat, setCloseChat, setUpdateChat, setUpdateConversation } = useAppActions()
    const { getExtensionStatus, openExtensionInTab, storeTabId, registerAppWithExtension } = useExtensionRequest()

    const { store: { extensionId, sidebar, extensionInstalled, extensionVersion } } = useAppStore()
    const sidebarRef = useRef(sidebar);

    useEffect(() => {
        sidebarRef.current = sidebar;
    }, [sidebar]);
    const { store: { user } } = useAuthStore()

    interface IToastPayload {
        duration?: number,
        showSpinner?: boolean,
        message: string,
        onClick?: {
            onClickHandler?: (e: any) => void;
            onCloseHandler?: (e: any) => void;
            label?: string;
            textDecoration?: string;
            keepShowingOnClick?: boolean;
        }
    }

    interface IModalPayload {
        headingText?: ReactNode
        descriptionText?: ReactNode
        descriptionTextBold?: ReactNode
        cancelBtnTitle?: ReactNode
        confirmBtnTitle?: ReactNode
        onConfirmHandler?: (e: any) => void
        onCancelHandler?: (e: any) => void
    }

    const showSuccessToast = (payload: IToastPayload) => setToast({ type: 'success', ...payload })
    const showInfoToast = (payload: IToastPayload) => setToast({ type: "info", ...payload })
    const showWarningToast = (payload: IToastPayload) => setToast({ type: 'warning', ...payload })
    const showErrorToast = (payload: IToastPayload) => setToast({ type: 'error', ...payload })
    const showReSendMessageToast = (payload: IToastPayload) => setToast({ type: 'reSendMessage', ...payload })
    const showRemovalScheduledToast = (payload: IToastPayload) => setToast({ type: 'removalScheduled', ...payload })
    const hideToast = () => setToast()

    const setModalSample = () => setModal({ type: ModalActionType.MODAL_SAMPLE })

    const setCancelFlow = (props?: any) => setModal({ type: ModalActionType.MODAL_CANCEL_FLOW, ...(props ? { props } : {}) })
    const setPauseFlow = (props?: any) => setModal({ type: ModalActionType.MODAL_PAUSE_FLOW, ...(props ? { props } : {}) })
    const setPaymentConfirmModal = () => setModal({ type: ModalActionType.PAYMENT_CONFIRM_MODAL })
    const setCustomColorModalHandler = (color: string, onColorChangedHandler: (color: string) => void) => setModal({ type: ModalActionType.CUSTOM_COLOR_MODAL, props: {title: 'Custom Tag Color', colorProp: color, onColorChangedHandler } })
    const setSyncConnectionsModalHandler = (syncCallback: (user: IUser) => void) => setModal({ type: ModalActionType.SYNC_CONNECTIONS_MODAL, props: { onClickHandler: syncCallback} })
    const setDisconnectConnectionsModalHandler = ( disconnectCallback: (disconnectFromLI: boolean, removeFromLD: boolean) => void, newDisconnectedCount?: number, selectedAllConnections?: boolean, numberOfConnections?: number, enableLinkedinAction?: boolean) => setModal({ type: ModalActionType.DISCONNECT_CONNECTIONS_MODAL, props: { onClickHandler: disconnectCallback, newDisconnectedCount: newDisconnectedCount, isAllSelected: selectedAllConnections, numberOfConnections: numberOfConnections, enableLinkedinAction: enableLinkedinAction } })
    const setExportConnectionsModalHandler = ( exportCallback: (includeTags: boolean, includeNotes: boolean, selectAll: boolean) => void, numberOfConnections?: number, selectedAllConnections?: boolean) => setModal({ type: ModalActionType.EXPORT_CONNECTIONS_MODAL, props: { onClickHandler: exportCallback, numberOfConnections: numberOfConnections, selectedAllConnections: selectedAllConnections } })
    const setExportToHubspotModalHandler = ( 
        exportCallback: (
            includeTags: boolean, 
            includeNotes: boolean, 
            selectAll: boolean, 
            useDefaultMapping: boolean, 
            exportType: 'contacts' | 'list' | 'workflow' | 'deals', 
            exportDetails?: {
                listId?: string;
                workflowId?: string;
                dealStage?: string;
                dealName?: string;
                amount?: number;
                pipelineId?: string;
            }
        ) => void, 
        numberOfConnections?: number, 
        selectedAllConnections?: boolean
    ) => setModal({ type: ModalActionType.EXPORT_TO_HUBSPOT_MODAL, props: { onClickHandler: exportCallback, numberOfConnections: numberOfConnections, selectedAllConnections: selectedAllConnections } })
    const setScheduleDisconnectModalHandler = ( 
        disconnectCallback: (enteredNumOfConnections: number) => void, selectedAllConnections?: boolean, numberOfConnections?: number, enableLinkedinAction?: boolean) => setModal({ type: ModalActionType.SCHEDULE_DISCONNECT_MODAL, props: { onClickHandler: disconnectCallback, isAllSelected: selectedAllConnections, numberOfConnections: numberOfConnections, enableLinkedinAction: enableLinkedinAction } })
    const setCalculateEnrichmentModalHandler = (
        enrichCallback: (selectedConnections: IConnection[], privateEmailChecked: boolean, businessEmailChecked: boolean, phoneChecked: boolean, enrichedTransactionId: string) => void,
        onEnrichedTransactionChange: (enrichedTransaction: IEnrichedTransaction) => void,
        selectedAllConnections?: boolean,
        selectedConnections?: IConnection[],
        csvRows?: any[],
        phoneChecked?: boolean,
        privateEmailChecked?: boolean,
        businessEmailChecked?: boolean,
        isBuyCredits?: boolean,
        showConfetti?: () => void,
        noCreditsLeft?: boolean
    ) => setModal({
        type: ModalActionType.CALCULATE_ENRICHMENT_MODAL,
        props: {
            onClickHandler: enrichCallback,
            onEnrichedTransactionChange,
            isAllSelected: selectedAllConnections,
            selectedConnections: selectedConnections,
            csvRows: csvRows,
            phoneChecked: phoneChecked,
            privateEmailChecked: privateEmailChecked,
            businessEmailChecked: businessEmailChecked,
            isBuyCredits: isBuyCredits,
            showConfetti: showConfetti,
            noCreditsLeft: noCreditsLeft
        }
    })
    const setEnrichmentCompleteModalHandler = (
        enrichedTransaction: IEnrichedTransaction
    ) => setModal({
        type: ModalActionType.ENRICHMENT_COMPLETE_MODAL,
        props: { enrichedTransaction }
    })
    const setHubspotExportResultModalHandler = (payload: {
        senderName?: string;
        exportType: string;
        total?: number;
        successful?: number;
        failed?: number;
        createdAt?: string;
        failedList?: string;
        connectionIds?: string;
    }) => setModal({ type: ModalActionType.HUBSPOT_EXPORT_RESULT_MODAL, props: payload })
    const setConfirmationModal = (payload: IModalPayload) => setModal({ type: ModalActionType.CONFIRMATION_MODAL, ...payload })
    const setDestructiveModal = (payload: IModalPayload) => setModal({ type: ModalActionType.DESTRUCTIVE_MODAL, ...payload })
    const setMessageModal = (payload: IModalPayload) => setModal({ type: ModalActionType.MESSAGE_MODAL, ...payload })
    const setConnectionsOverviewModal = (payload: any) => setModal({ type: ModalActionType.CONNECTIONS_OVERVIEW_MODAL, ...payload })
    const setCsvOverviewModal = (payload: any) => setModal({ type: ModalActionType.CSV_OVERVIEW_MODAL, ...payload })
    const setRemovalOverviewModal = () => setModal({ type: ModalActionType.REMOVAL_OVERVIEW_MODAL })
    const setCompanyOverviewModal = (companyId: string) => setModal({ type: ModalActionType.COMPANY_OVERVIEW_MODAL, props: { companyId } })
    const setBulkEditTagsModal = (
        selectedConnectionsIds?: string[],
        selectedAllConnections?: boolean,
        connectionTagSnapshots?: IBulkEditTagsConnectionSnapshot[],
        options?: { closeOnBackdropClick?: boolean }
    ) =>
        setModal({
            type: ModalActionType.BULK_EDIT_TAGS_MODAL,
            props: {
                selectedConnectionsIds,
                selectedAllConnections,
                connectionTagSnapshots,
                closeOnBackdropClick: options?.closeOnBackdropClick === true,
            },
        })
    const setCreateFeedModal = (selectedConnectionsIds?: string[], disableExistingFeed?: boolean, onClose?: () => void, urlMode?: boolean, selectedFeedId?: string) => setModal({ type: ModalActionType.CREATE_FEED_MODAL, props: { selectedConnectionsIds, disableExistingFeed, onClose, urlMode, selectedFeedId } })
    const setCreateFeedGuideModal = () => setModal({ type: ModalActionType.CREATE_FEED_GUIDE_MODAL, props: { onClose: hideModal } })
    const setRenameFeedModal = (feedName: string, onConfirm: (newName: string) => void) => setModal({ type: ModalActionType.RENAME_FEED_MODAL, props: { feedName, onConfirm, onCancel: hideModal } })
    const setChooseTemplateModal = (onMessageSaveHandler?: (e: string) => void, onCloseHandler?: () => void) => setModal({ type: ModalActionType.CHOOSE_TEMPLATE_MODAL, props: { onMessageSaveHandler, onCloseHandler } })
    const setAddMembersModal = () => setModal({ type: ModalActionType.ADD_MEMBERS_MODAL })
    const setCreateCustomFieldModal = (onClose?: () => void, editField?: { key: string; label: string; type: string }) => setModal({ type: ModalActionType.CREATE_CUSTOM_FIELD_MODAL, props: { onClose, editField } })
    const setNotFoundUrlsModalHandler = (notFoundIdentifiers: string[], onClose?: () => void) => setModal({ type: ModalActionType.NOT_FOUND_URLS_MODAL, props: { notFoundIdentifiers, onClose: onClose || hideModal } })
    const setEditKanbanModal = (kanban: IKanban | undefined, onConfirm: (data: any) => Promise<void>, onClose?: () => void, onDelete?: () => void, allKanbansCount?: number) => setModal({ type: ModalActionType.EDIT_KANBAN_MODAL, props: { kanban, onConfirm, onClose, onDelete, allKanbansCount } })
    const setAddToKanbanModal = (selectedConnections?: any[], onClose?: () => void) => setModal({ type: ModalActionType.ADD_TO_KANBAN_MODAL, props: { selectedConnections, onClose } })
    const setAddToCampaignModal = (props: {
        selectedConnections?: any[];
        selectedAllConnections?: boolean;
        total?: number;
        onClose?: () => void;
    }) => setModal({ type: ModalActionType.ADD_TO_CAMPAIGN_MODAL, props })
    const setMoveToCampaignModal = (props: {
        sourceCampaignId: string;
        count: number;
        campaigns: ICampaign[];
        campaignsReady: boolean;
        leadIds?: string[];
        all?: boolean;
        status?: string;
        source?: string;
        search?: string;
        onMoved?: (result: { moved: number; alreadyEnrolled: number; suppressed: number }) => void;
    }) => setModal({ type: ModalActionType.MOVE_TO_CAMPAIGN_MODAL, props })
    const setCampaignsAnnouncementModal = (onContinue?: () => void) => setModal({ type: ModalActionType.CAMPAIGNS_ANNOUNCEMENT_MODAL, props: { onContinue } })
    const setAddToSuppressionListModal = (props: {
        listId?: string;
        initialType?: SuppressionType;
        initialValue?: string;
        source?: 'manual' | 'lead';
        onAdded?: (listId: string) => void;
    }) => setModal({ type: ModalActionType.ADD_TO_SUPPRESSION_LIST_MODAL, props })
    /** Create a suppression list, or rename / rescope `list`. */
    const setSuppressionListFormModal = (props: { list?: ISuppressionList; onSaved?: (listId: string) => void }) =>
        setModal({ type: ModalActionType.SUPPRESSION_LIST_FORM_MODAL, props })
    const hideModal = () => setModal()

    const showSpinner = () => setSpinner(true)
    const hideSpinner = () => setSpinner(false)

    
    const openSidebar = (type: string, props?: any) => {
        const currentSidebar = sidebarRef.current;
        let zIndex: number = 1000000;
        zIndex = currentSidebar.length > 0 ? currentSidebar[currentSidebar.length - 1].zIndex + 1 : zIndex
        const nextSidebar = [...currentSidebar, { type, zIndex, props }];
        sidebarRef.current = nextSidebar;
        setSidebar(nextSidebar)
    } 

    //This relies heavily on the logic that user won't see other sidebars
    const closeSidebar = (type: string) => {
        const currentSidebar = sidebarRef.current;
        const newSidebar = currentSidebar.filter((sidebarItem: ISidebar) => sidebarItem.type !== type)
        sidebarRef.current = newSidebar;
        setSidebar([...newSidebar])
    }

    const checkExtensionStatus = async (): Promise<boolean> => {
        let result = await getExtensionStatus()
        const isInstalled = result?.status === 'installed'
        const wasInstalled = !!extensionInstalled

        const nextInstalled = Boolean(isInstalled)
        if (extensionInstalled !== nextInstalled) {
            setExtensionInstalled(nextInstalled)
        }
        const nextVersion = isInstalled ? result?.version : undefined
        if (extensionVersion !== nextVersion) {
            setExtensionVersion(nextVersion)
        }

        if (isInstalled && !wasInstalled) {
            await registerAppWithExtension()
        }

        // First successful handshake tracking
        try {
            if (isInstalled && !wasInstalled && user?._id) {

                console.log('checkExtensionStatus', {
                    isInstalled: isInstalled,
                    wasInstalled: wasInstalled,
                    user: user?._id,
                })

                const flagKey = `ph_chrome_extension_installed_${user._id}`
                const alreadyTracked = localStorage.getItem(flagKey) === 'true'
                if (!alreadyTracked) {
                    const installSource =
                        sessionStorage.getItem('ld_extension_install_source') ||
                        'unknown'

                    const createdAtRaw = (user as any)?.createdAt || (user as any)?.created_at
                    const createdAtMs = createdAtRaw ? new Date(createdAtRaw).getTime() : NaN
                    const minutes =
                        Number.isFinite(createdAtMs)
                            ? Math.max(0, Math.round((Date.now() - createdAtMs) / 60000))
                            : undefined

                    posthog.capture('chrome extension installed', {
                        install_source: installSource,
                        ...(minutes !== undefined ? { time_since_signup_minutes: minutes } : {}),
                    })

                    console.log('chrome extension installed', {
                        install_source: installSource,
                        ...(minutes !== undefined ? { time_since_signup_minutes: minutes } : {}),
                    })

                    localStorage.setItem(flagKey, 'true')
                    sessionStorage.removeItem('ld_extension_install_source')
                }
            }
        } catch {}

        return isInstalled
    }

    const syncExtensionId = (appId?: string) => {
        if (!appId) {
            appId = localStorage.getItem('appId') ?? process.env.REACT_APP_EXTENSION_ID
        }
        if(appId) localStorage.setItem('appId', appId)
        setExtensionId(appId)
    }

    const openExtension = ({ query }: any = {}) => {
        storeTabId('storeAppTabId')
        openExtensionInTab({query});
    };

    const buildExtensionUrl = ({ query }: any = {}) => {
        return `chrome-extension://${extensionId}/src/options/index.html${query ? `#${query}` : ''}`;
    };

    const setLeadDeltaTabIsActiveHandler = (payload: boolean) => {
        setLeadDeltaTabIsActive(payload)
    }

    const openChatHandler = async ( user: any, connection: any, conversation: any, messages: any, prefilledMessage?: string) => {

        setOpenChat({
            id: connection._id,
            convId: conversation?.[0]?._id,
            conversationId: conversation?.[0]?.conversationId,
            profileId: connection.profileId,
            userId: user._id,
            user: {
                firstName: user.linkedinData.firstName,
                lastName: user.linkedinData.lastName,
                picture: user.linkedinData.profilePicture,
            },
            sender: {
                firstName: connection?.firstName,
                lastName: connection?.lastName,
                profilePicture: connection?.profilePicture
            },
            messages: conversation?.[0]?.conversationId ? messages.map((msg: any) => ({ ...msg, isDelivered: true })) : [],
            ...(prefilledMessage ? { prefilledMessage } : {}),
            connection: connection
        })
    }

    const updateChatHandler = (id: string, messages: any, conversationId?: any) => {
        setUpdateChat(id, messages, conversationId);
    }

    const updateConversationHandler = (props: any) => {
        setUpdateConversation(props);
    }

    const closeChatHandler = (connectionId: string) => {
        setCloseChat(connectionId);
    }

    return {
        showSuccessToast,
        showInfoToast,
        showWarningToast,
        showErrorToast,
        showReSendMessageToast,
        showRemovalScheduledToast,
        hideToast,
        setModalSample,
        setCancelFlow,
        setPauseFlow,
        setPaymentConfirmModal,
        setConfirmationModal,
        setDestructiveModal,
        setMessageModal,
        setConnectionsOverviewModal,
        setCsvOverviewModal,
        hideModal,
        showSpinner,
        hideSpinner,
        checkExtensionStatus,
        syncExtensionId,
        openExtension,
        openSidebar,
        closeSidebar,
        setCustomColorModalHandler,
        buildExtensionUrl,
        setSyncConnectionsModalHandler,
        setDisconnectConnectionsModalHandler,
        setExportConnectionsModalHandler,
        setExportToHubspotModalHandler,
        setLeadDeltaTabIsActiveHandler,
        setScheduleDisconnectModalHandler,
        setRemovalOverviewModal,
        setAddMembersModal,
        setCompanyOverviewModal,
        setBulkEditTagsModal,
        setCreateFeedModal,
        setCreateFeedGuideModal,
        setRenameFeedModal,
        setCalculateEnrichmentModalHandler,
        setEnrichmentCompleteModalHandler,
        setHubspotExportResultModalHandler,
        openChatHandler,
        closeChatHandler,
        updateChatHandler,
        updateConversationHandler,
        setChooseTemplateModal,
        setCreateCustomFieldModal,
        setNotFoundUrlsModalHandler,
        setEditKanbanModal,
        setAddToKanbanModal,
        setAddToCampaignModal,
        setMoveToCampaignModal,
        setCampaignsAnnouncementModal,
        setAddToSuppressionListModal,
        setSuppressionListFormModal
    }
}